import { useEffect, useState } from 'react';
import { BookOpen, FolderOpen, Plus, Pencil, Trash2, FileDown, Upload, Download, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import UsualMealEditorModal from './UsualMealEditorModal';
import { exportUsualMealPdf, formatMacroTotals } from '../utils/usualMealPdf';

type Props = {
  student: { id: string; firstName?: string; lastName?: string };
  onClose: () => void;
};

export default function StudentUsualMealsAndFilesPanel({ student, onClose }: Props) {
  const [tab, setTab] = useState<'meals' | 'files'>('meals');
  const [meals, setMeals] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingMeal, setEditingMeal] = useState<any>(null);
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const load = async () => {
    setLoading(true);
    const sid = student.id;
    const [mealsRes, filesRes] = await Promise.allSettled([
      api.get(`/trainer/students/${sid}/usual-meals`),
      api.get(`/trainer/students/${sid}/files`),
    ]);
    if (mealsRes.status === 'fulfilled') {
      setMeals(mealsRes.value.data || []);
    } else {
      setMeals([]);
      const msg = mealsRes.reason?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join(', ') : (msg || 'No se pudieron cargar las comidas habituales'));
    }
    if (filesRes.status === 'fulfilled') {
      setFiles(filesRes.value.data || []);
    } else {
      setFiles([]);
      const msg = filesRes.reason?.response?.data?.message;
      toast.error(Array.isArray(msg) ? msg.join(', ') : (msg || 'No se pudieron cargar los archivos'));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [student.id]);

  const deleteMeal = async (mealId: string) => {
    if (!confirm('¿Eliminar esta comida habitual?')) return;
    try {
      await api.delete(`/trainer/students/${student.id}/usual-meals/${mealId}`);
      setMeals((prev) => prev.filter((m) => m.id !== mealId));
      toast.success('Eliminada');
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const uploadFile = async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post(`/trainer/students/${student.id}/files`, fd);
      setFiles((prev) => [r.data, ...prev]);
      toast.success('Archivo subido');
    } catch {
      toast.error('Error al subir');
    }
  };

  const renameFile = async () => {
    if (!renameId || !renameValue.trim()) return;
    try {
      const r = await api.patch(`/trainer/students/${student.id}/files/${renameId}`, {
        originalName: renameValue.trim(),
      });
      setFiles((prev) => prev.map((f) => (f.id === renameId ? r.data : f)));
      setRenameId(null);
      toast.success('Renombrado');
    } catch {
      toast.error('No se pudo renombrar');
    }
  };

  const deleteFile = async (fileId: string) => {
    if (!confirm('¿Eliminar este archivo?')) return;
    try {
      await api.delete(`/trainer/students/${student.id}/files/${fileId}`);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success('Archivo eliminado');
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  const downloadFile = async (file: any) => {
    try {
      const r = await api.get(`/trainer/students/${student.id}/files/${file.id}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName || 'archivo';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 className="font-bold text-gray-800">
              {student.firstName} {student.lastName}
            </h3>
            <p className="text-xs text-gray-500">Comidas habituales y archivos</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="flex gap-2 px-4 pt-3">
          <button
            type="button"
            className={`text-sm px-3 py-1.5 rounded-lg font-medium ${tab === 'meals' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => setTab('meals')}
          >
            <span className="inline-flex items-center gap-1"><BookOpen size={14} /> Habituales</span>
          </button>
          <button
            type="button"
            className={`text-sm px-3 py-1.5 rounded-lg font-medium ${tab === 'files' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'}`}
            onClick={() => setTab('files')}
          >
            <span className="inline-flex items-center gap-1"><FolderOpen size={14} /> Archivos</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-8">Cargando…</p>
          ) : tab === 'meals' ? (
            <div className="space-y-3">
              <button
                type="button"
                className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  setEditingMeal({
                    name: '',
                    mealType: 'LUNCH',
                    items: [],
                    ingredientsText: '',
                    cookTimeMinutes: null,
                    notes: '',
                    photoUrls: [],
                  });
                  setEditorOpen(true);
                }}
              >
                <Plus size={14} /> Nueva comida habitual
              </button>
              {meals.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Sin comidas habituales</p>
              ) : (
                <div className="space-y-2">
                  {meals.map((meal) => (
                    <div key={meal.id} className="border border-gray-200 rounded-xl p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 text-sm">{meal.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {formatMacroTotals(meal)}
                            {meal.cookTimeMinutes != null ? ` · ${meal.cookTimeMinutes} min` : ''}
                          </p>
                          {meal.ingredientsText ? (
                            <p className="text-xs text-gray-400 mt-1 line-clamp-2">{meal.ingredientsText}</p>
                          ) : null}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button type="button" className="p-1.5 rounded-lg hover:bg-gray-100" title="Editar"
                            onClick={() => { setEditingMeal(meal); setEditorOpen(true); }}>
                            <Pencil size={14} className="text-gray-600" />
                          </button>
                          <button type="button" className="p-1.5 rounded-lg hover:bg-gray-100" title="PDF"
                            onClick={() => {
                              void exportUsualMealPdf(meal).then((ok) => {
                                if (!ok) toast.error('No se pudo abrir el PDF. Revisa el bloqueo de ventanas emergentes.');
                              });
                            }}>
                            <FileDown size={14} className="text-gray-600" />
                          </button>
                          <button type="button" className="p-1.5 rounded-lg hover:bg-red-50" title="Eliminar"
                            onClick={() => deleteMeal(meal.id)}>
                            <Trash2 size={14} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <label className="btn-primary text-sm inline-flex items-center gap-1 cursor-pointer">
                <Upload size={14} /> Subir archivo
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadFile(f);
                    e.target.value = '';
                  }}
                />
              </label>
              {files.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Sin archivos</p>
              ) : (
                <div className="space-y-2">
                  {files.map((file) => (
                    <div key={file.id} className="border border-gray-200 rounded-xl p-3">
                      {renameId === file.id ? (
                        <div className="flex gap-2">
                          <input className="input flex-1 text-sm" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
                          <button type="button" className="btn-primary text-xs" onClick={renameFile}>OK</button>
                          <button type="button" className="btn-secondary text-xs" onClick={() => setRenameId(null)}>Cancelar</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{file.originalName}</p>
                            <p className="text-xs text-gray-400">{Math.round((file.sizeBytes || 0) / 1024)} KB</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button type="button" className="p-1.5 rounded-lg hover:bg-gray-100" title="Descargar" onClick={() => downloadFile(file)}>
                              <Download size={14} />
                            </button>
                            <button type="button" className="p-1.5 rounded-lg hover:bg-gray-100" title="Renombrar"
                              onClick={() => { setRenameId(file.id); setRenameValue(file.originalName || ''); }}>
                              <Pencil size={14} />
                            </button>
                            <button type="button" className="p-1.5 rounded-lg hover:bg-red-50" title="Eliminar" onClick={() => deleteFile(file.id)}>
                              <Trash2 size={14} className="text-red-500" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <UsualMealEditorModal
        open={editorOpen}
        initial={editingMeal}
        studentId={student.id}
        onClose={() => { setEditorOpen(false); setEditingMeal(null); }}
        onSaved={() => load()}
      />
    </div>
  );
}
